import React, {FC, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {AuthPageLayout} from '../../components/auth-page-layout/auth-page-layout.component';
import {InputSubmitButton} from '../../components/input-submit-btn/input-submit-btn.component';
import {PageTitle} from '../../components/page-title/page-title.component';
import { ApiService } from '../../services/api';

type NewPasswordFormValues = {
  newPassword: string;
  confirmPassword: string;
};

export const UpdatePassword: FC = () => {

  const [searchParams] = useSearchParams();

  const resetguid = searchParams.get('resetguid');

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: {errors},
    watch,
    clearErrors
  } = useForm<NewPasswordFormValues>();

  const [responseError, setResponseError] = useState<string>();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const resetErrors = () => { 
    setResponseError(undefined);
    clearErrors();
  }

  const onSubmit = handleSubmit(async data => {
    if (submitting) { 
      return;
    }
    try {
      setSubmitting(true);
      setResponseError(undefined);
      const response = await ApiService.updatePassword(String(resetguid), data.newPassword);
      if (response) { 
        navigate('/', {replace: true});
      } else { 
        setResponseError('Something went wrong.');
      }
    } catch (e) {
      setResponseError('Your email or password are not correct, please try again or reset your password.');
    } finally { 
      setSubmitting(false);
    }
  });

  return (
    <AuthPageLayout padTop>
      <PageTitle>Forgot Password</PageTitle>

      <p className="whitespace-normal text-center my-2">
        Please ensure your new password has 10 characters including a capital letter, special character and number.
      </p>

      <form onSubmit={onSubmit} className="flex flex-col gap-4 my-2">
        <input
          aria-label="Password"
          aria-invalid={errors.newPassword ? 'true' : 'false'}
          type="password"
          placeholder="Password"
          className={`w-full bg-gray-200 rounded-md px-3 py-4 ${errors?.newPassword && 'border border-trak-red'}`}
          {...register('newPassword', {
            required: {
              value: true,
              message: 'Field required',
            },
            minLength: {
              value: 10,
              message: 'Password must be at least 10 characters',
            },
            pattern: {
              value: /^(?=.*?[A-Z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/i,
              message: 'Password must contain a capital letter, special character and a number.',
            },
          })}
          onChange={resetErrors}
        />

        {errors?.newPassword && <p className="text-trak-red whitespace-normal">{errors.newPassword.message}</p>}

        <input
          aria-label="Confirm Password"
          aria-invalid={errors.confirmPassword ? 'true' : 'false'}
          type="password"
          placeholder="Confirm Password"
          className={`w-full bg-gray-200 rounded-md px-3 py-4 ${errors?.confirmPassword && 'border border-trak-red'}`}
          {...register('confirmPassword', {
            required: {
              value: true,
              message: 'Field required',
            },
            validate: (v: string) => {
              if (watch('newPassword') != v) {
                return 'Passwords do not match';
              }
            },
          })}
          onChange={resetErrors}
        />

        {errors?.confirmPassword && <p className="text-trak-red whitespace-normal">{errors.confirmPassword.message}</p>}

        {responseError && <p className="text-trak-red whitespace-normal">{responseError}</p>}

        <InputSubmitButton value="Update Password" />
      </form>
    </AuthPageLayout>
  );
};
